/* ══════════════════════════════════════════════════════════════
   BRUTECH CART GUARD
   PC Builder components are not sold on their own. The buy buttons are
   gone from every theme surface, but a part can still reach a cart from an
   old open tab, a cached page, a direct link, or a cart built before the
   lock existed. This sweeps those out.

   How it decides: it asks the theme, not the browser. cart.js does not
   carry tags or collections, so the check is rendered server side by
   sections/brutech-cart-guard.liquid (Section Rendering API, the same way
   the theme loads its variant-added notice). That section applies
   snippets/brutech-builder-lock.liquid, the one definition of what counts
   as a builder component, and ignores any line the PC Builder itself
   deployed (those carry the hidden _bt_loadout property).

   When it runs: once per page load when the cart is not empty, and again
   shortly after anything is added to the cart (it watches fetch calls to
   /cart/add, so every custom add path is covered, not just the theme's own).

   It never breaks the shop: any failure is swallowed and the cart is left
   alone. The cart page still blocks checkout on its own as a fallback.
══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  if (window.__brtCartGuard) return;
  window.__brtCartGuard = true;

  var SECTION      = 'brutech-cart-guard';
  var NOTICE_KEY   = 'brt-cart-guard-notice';
  var RELOAD_KEY   = 'brt-cart-guard-reloads';
  var BUILDER_URL  = '/pages/custom-pc-builder';
  var DEBOUNCE_MS  = 350;

  /* Keep our own reference so the guard's requests never trip the add hook. */
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!nativeFetch) return;

  var running = false;
  var queued  = false;
  var timer   = null;

  function root() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r || '/';
  }

  function ssGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, val) {
    try { window.sessionStorage.setItem(key, val); } catch (e) { /* storage blocked */ }
  }
  function ssDel(key) {
    try { window.sessionStorage.removeItem(key); } catch (e) { /* storage blocked */ }
  }

  /* ── Notice toast ────────────────────────────────────────────
     Hard-coded values on purpose: this is a body-appended, fixed element,
     so it must not depend on any custom property from the theme. */
  function showToast(titles) {
    if (!titles || !titles.length || !document.body) return;
    var old = document.getElementById('brt-guard-toast');
    if (old && old.parentNode) old.parentNode.removeChild(old);

    var box = document.createElement('div');
    box.id = 'brt-guard-toast';
    box.setAttribute('role', 'status');
    box.setAttribute('aria-live', 'polite');
    box.style.cssText =
      'position:fixed;left:50%;transform:translateX(-50%);' +
      'bottom:calc(16px + env(safe-area-inset-bottom, 0px));' +
      'width:calc(100% - 32px);max-width:520px;box-sizing:border-box;' +
      'z-index:2147483000;padding:14px 44px 14px 16px;' +
      'background:#14131a;color:#ffffff;border:1px solid #27bcdd;border-radius:12px;' +
      'box-shadow:0 10px 30px rgba(0,0,0,0.45);' +
      'font:14px/1.45 Poppins,system-ui,-apple-system,Segoe UI,Roboto,sans-serif;';

    var head = document.createElement('div');
    head.style.cssText = 'font-weight:700;margin-bottom:4px;color:#27bcdd;';
    head.textContent = titles.length > 1 ? 'Parts removed from your cart' : 'Part removed from your cart';
    box.appendChild(head);

    var body = document.createElement('div');
    body.textContent = titles.join(', ') + (titles.length > 1 ? ' are' : ' is') +
      ' only sold as part of a complete loadout in the PC Builder.';
    box.appendChild(body);

    var link = document.createElement('a');
    link.href = BUILDER_URL;
    link.textContent = 'Open the PC Builder';
    link.style.cssText = 'display:inline-block;margin-top:8px;font-weight:700;color:#ffffff;text-decoration:underline;';
    box.appendChild(link);

    var close = document.createElement('button');
    close.type = 'button';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '✕';
    close.style.cssText =
      'position:absolute;top:8px;right:8px;width:32px;height:32px;border:0;border-radius:8px;' +
      'background:transparent;color:#ffffff;font-size:16px;line-height:1;cursor:pointer;';
    close.addEventListener('click', function () {
      if (box.parentNode) box.parentNode.removeChild(box);
    });
    box.appendChild(close);

    document.body.appendChild(box);
    window.setTimeout(function () {
      if (box.parentNode) box.parentNode.removeChild(box);
    }, 14000);
  }

  /* A notice saved before a reload is shown once, then cleared. */
  function showPendingNotice() {
    var raw = ssGet(NOTICE_KEY);
    if (!raw) return;
    ssDel(NOTICE_KEY);
    var titles;
    try { titles = JSON.parse(raw); } catch (e) { titles = null; }
    if (titles && titles.length) showToast(titles);
  }

  /* Never reload more than twice in 30 seconds, whatever happens. */
  function mayReload() {
    var now = Date.now();
    var list;
    try { list = JSON.parse(ssGet(RELOAD_KEY) || '[]'); } catch (e) { list = []; }
    list = list.filter(function (t) { return now - t < 30000; });
    if (list.length >= 2) return false;
    list.push(now);
    ssSet(RELOAD_KEY, JSON.stringify(list));
    return true;
  }

  /* ── Remove the flagged lines, one at a time ─────────────────── */
  function removeAll(blocked) {
    var removed = [];
    var failed  = [];

    return blocked.reduce(function (chain, line) {
      return chain.then(function () {
        return nativeFetch(root() + 'cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: 0 })
        })
        .then(function (r) {
          if (r.ok) removed.push(line.title || 'A builder part');
          else failed.push(line.title || 'A builder part');
        })
        .catch(function () { failed.push(line.title || 'A builder part'); });
      });
    }, Promise.resolve()).then(function () {
      if (removed.length) {
        /* The header cart, cart drawer and cart page all hold their own copy
           of the cart. A reload is the only way to be sure none is stale. */
        ssSet(NOTICE_KEY, JSON.stringify(removed));
        if (mayReload()) { window.location.reload(); return; }
        showPendingNotice();
      } else if (failed.length) {
        showToast(failed);
      }
    });
  }

  /* ── Ask the theme which cart lines are builder-only ─────────── */
  function check() {
    if (running) { queued = true; return; }
    running = true;

    nativeFetch(root() + '?sections=' + SECTION + '&_=' + Date.now(), {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
    .then(function (r) {
      if (!r.ok) throw new Error('guard ' + r.status);
      return r.json();
    })
    .then(function (json) {
      var html = json && json[SECTION];
      if (!html) return [];
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el  = doc.getElementById('brt-cart-guard-data');
      if (!el) return [];
      var data = JSON.parse(el.textContent);
      return (data && data.blocked) || [];
    })
    .then(function (blocked) {
      if (!blocked.length) return null;
      return removeAll(blocked);
    })
    .catch(function () { /* fail open, never break the shop */ })
    .then(function () {
      running = false;
      if (queued) { queued = false; schedule(); }
    });
  }

  function schedule() {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(function () { timer = null; check(); }, DEBOUNCE_MS);
  }

  /* ── Watch every add to cart, theme or custom ────────────────── */
  var ADD_RE = /\/cart\/add(\.js)?(\?|$)/;
  var wrapped = window.fetch;
  window.fetch = function (input) {
    var p = wrapped.apply(window, arguments);
    try {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (ADD_RE.test(url)) {
        p.then(function (res) { if (res && res.ok) schedule(); }, function () {});
      }
    } catch (e) { /* never interfere with the real call */ }
    return p;
  };

  /* Theme and Brutech cart events, caught in the capture phase so it does
     not matter where they were dispatched or whether they bubble. */
  ['variant:add', 'cart:change', 'cart:updated', 'cart:refresh'].forEach(function (name) {
    window.addEventListener(name, schedule, true);
  });

  /* Back/forward cache restores a page without running scripts again. */
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) schedule();
  });

  function init() {
    showPendingNotice();
    /* theme.liquid prints the cart count on the html element. If it is
       missing, check anyway rather than assume the cart is empty. */
    var raw = document.documentElement.getAttribute('data-brt-cart-count');
    if (raw === null || parseInt(raw, 10) > 0) schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
